import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Grid, Button, Chip,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, FormControlLabel, Switch, Tooltip, IconButton,
} from '@mui/material';
import {
  ArrowBack, Add, Person, CalendarToday, MedicalServices,
  WarningAmber, PriorityHigh, OpenInNew, Refresh,
} from '@mui/icons-material';
import { wardService, ipdService, userService, patientService } from '../../services/api';

const STATUS_STYLE = {
  Available:        { bg: '#e8f5e9', border: '#4caf50', text: '#1b5e20', label: 'AVAILABLE' },
  Occupied:         { bg: '#fff3f3', border: '#ef5350', text: '#b71c1c', label: 'OCCUPIED'  },
  Reserved:         { bg: '#fff8e1', border: '#ffa726', text: '#e65100', label: 'RESERVED'  },
  UnderMaintenance: { bg: '#f5f5f5', border: '#9e9e9e', text: '#424242', label: 'MAINTENANCE' },
  Housekeeping:     { bg: '#e3f2fd', border: '#42a5f5', text: '#0d47a1', label: 'HOUSEKEEPING' },
};

const ADMISSION_TYPES = ['Emergency','Elective','Transfer','Referral'];

const calcAge = (dob) => {
  if (!dob) return null;
  return Math.floor((new Date() - new Date(dob)) / (1000 * 60 * 60 * 24 * 365.25));
};

const toLocalDT = (d) => {
  const dt = new Date(d);
  const pad = n => String(n).padStart(2,'0');
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const WardBedView = () => {
  const { wardId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canEdit = user.role === 'Admin' || user.role === 'Doctor' || user.role === 'Receptionist';

  const [wardData, setWardData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Patient panel (click on occupied bed)
  const [patientPanel, setPatientPanel] = useState({ open: false, bed: null });

  // Admission dialog (click on available bed or New Admission button)
  const [admitDialog, setAdmitDialog] = useState({ open: false, prefillBed: null });
  const [admitForm, setAdmitForm] = useState({
    patientId: null, bedId: '', admittingDoctorId: null,
    admissionType: 'Elective', admissionDate: toLocalDT(new Date()),
    expectedDischargeDate: '', admissionDiagnosis: '', icd10Code: '',
    isMlc: false, mlcDetails: '', referredBy: '', referralFrom: '',
  });
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [admitError, setAdmitError] = useState('');
  const [admitting, setAdmitting] = useState(false);

  // Discharge dialog
  const [dischargeDialog, setDischargeDialog] = useState({ open: false, bed: null });
  const [dischargeForm, setDischargeForm] = useState({
    status: 'Discharged', dischargeType: 'Improved',
    dischargeNotes: '', actualDischargeDate: toLocalDT(new Date()),
  });
  const [discharging, setDischarging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await wardService.getBeds(wardId);
      setWardData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [wardId]);

  useEffect(() => { load(); }, [load]);

  // Load doctors for admission form
  useEffect(() => {
    if (!admitDialog.open) return;
    userService.getDoctors().then(r => setDoctors(r.data));
  }, [admitDialog.open]);

  // Patient search
  useEffect(() => {
    if (!patientSearch || patientSearch.length < 2) { setPatientOptions([]); return; }
    const t = setTimeout(async () => {
      try { const r = await patientService.searchPatients(patientSearch); setPatientOptions(r.data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [patientSearch]);

  const openAdmitDialog = (bed = null) => {
    setAdmitError('');
    setAdmitForm({
      patientId: null, bedId: bed ? String(bed.id) : '',
      admittingDoctorId: null, admissionType: 'Elective',
      admissionDate: toLocalDT(new Date()), expectedDischargeDate: '',
      admissionDiagnosis: '', icd10Code: '', isMlc: false,
      mlcDetails: '', referredBy: '', referralFrom: '',
    });
    setPatientOptions([]); setPatientSearch('');
    setAdmitDialog({ open: true, prefillBed: bed });
  };

  const handleAdmit = async (e) => {
    e.preventDefault();
    if (!admitForm.patientId) { setAdmitError('Please select a patient.'); return; }
    if (!admitForm.bedId)     { setAdmitError('Please select a bed.'); return; }
    setAdmitting(true);
    try {
      await ipdService.create({
        patientId: admitForm.patientId.id, bedId: parseInt(admitForm.bedId),
        wardId: parseInt(wardId), admittingDoctorId: admitForm.admittingDoctorId?.id || null,
        admissionType: admitForm.admissionType,
        admissionDate: admitForm.admissionDate || toLocalDT(new Date()),
        expectedDischargeDate: admitForm.expectedDischargeDate || null,
        admissionDiagnosis: admitForm.admissionDiagnosis, icd10Code: admitForm.icd10Code,
        isMlc: admitForm.isMlc, mlcDetails: admitForm.mlcDetails,
        referredBy: admitForm.referredBy, referralFrom: admitForm.referralFrom,
      });
      setAdmitDialog({ open: false, prefillBed: null });
      load();
    } catch (err) {
      setAdmitError(err.response?.data?.message || 'Failed to admit patient');
    } finally { setAdmitting(false); }
  };

  const openDischarge = (bed) => {
    setDischargeDialog({ open: true, bed });
    setDischargeForm({ status: 'Discharged', dischargeType: 'Improved', dischargeNotes: '', actualDischargeDate: toLocalDT(new Date()) });
  };

  const handleDischarge = async (e) => {
    e.preventDefault();
    setDischarging(true);
    try {
      await ipdService.discharge(dischargeDialog.bed.currentAdmission.id, {
        ...dischargeForm,
        actualDischargeDate: dischargeForm.actualDischargeDate || toLocalDT(new Date()),
      });
      setDischargeDialog({ open: false, bed: null });
      setPatientPanel({ open: false, bed: null });
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Discharge failed');
    } finally { setDischarging(false); }
  };

  const ward = wardData?.ward;
  const beds = wardData?.beds || [];
  const stats = {
    available: beds.filter(b => b.status === 'Available').length,
    occupied:  beds.filter(b => b.status === 'Occupied').length,
    reserved:  beds.filter(b => b.status === 'Reserved').length,
    other:     beds.filter(b => b.status !== 'Available' && b.status !== 'Occupied' && b.status !== 'Reserved').length,
  };

  // Available beds for the bed select (only in admission dialog)
  const selectableBeds = beds.filter(b => b.status === 'Available' || b.status === 'Reserved');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <Box sx={{ bgcolor: 'white', boxShadow: 1, px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton size="small" onClick={() => navigate('/ipd')}><ArrowBack /></IconButton>
          <Box>
            <Typography variant="h6" fontWeight={700}>{ward?.name || 'Ward'}</Typography>
            <Typography variant="caption" color="text.secondary">{ward?.wardType} {ward?.floor ? `· ${ward.floor}` : ''}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Mini stats */}
          {[
            { label: 'Available', val: stats.available, color: '#2e7d32' },
            { label: 'Occupied',  val: stats.occupied,  color: '#c62828' },
            { label: 'Reserved',  val: stats.reserved,  color: '#f57f17' },
          ].map(s => (
            <Box key={s.label} sx={{ textAlign: 'center', mx: 1 }}>
              <Typography fontWeight={700} sx={{ color: s.color }}>{s.val}</Typography>
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
            </Box>
          ))}
          <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
          {canEdit && (
            <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openAdmitDialog()}>
              Admit Patient
            </Button>
          )}
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <>
            {beds.length === 0 && (
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography color="text.secondary">No beds configured for this ward. Add beds in <strong>Ward Management</strong>.</Typography>
              </Paper>
            )}

            {/* Bed Grid */}
            <Grid container spacing={2}>
              {beds.map(bed => {
                const s = STATUS_STYLE[bed.status] || STATUS_STYLE.Available;
                const adm = bed.currentAdmission;
                const patient = adm?.patient;
                const age = calcAge(patient?.dateOfBirth);
                const isOccupied = bed.status === 'Occupied';

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={bed.id}>
                    <Paper
                      elevation={isOccupied ? 3 : 1}
                      sx={{
                        p: 0, borderRadius: 2, overflow: 'hidden',
                        border: `2px solid ${s.border}`,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                      }}
                      onClick={() => isOccupied ? setPatientPanel({ open: true, bed }) : canEdit && openAdmitDialog(bed)}
                    >
                      {/* Bed header */}
                      <Box sx={{ bgcolor: s.border, px: 2, py: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography fontWeight={700} sx={{ color: 'white', fontSize: '1rem' }}>{bed.bedNumber}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {adm?.isMlc && <Tooltip title="MLC Case"><PriorityHigh sx={{ fontSize: 14, color: 'white' }} /></Tooltip>}
                          <Typography variant="caption" sx={{ color: 'white', opacity: 0.9, fontWeight: 600 }}>{bed.bedType}</Typography>
                        </Box>
                      </Box>

                      {/* Bed body */}
                      <Box sx={{ p: 2, bgcolor: s.bg, minHeight: 110 }}>
                        {isOccupied && patient ? (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                              <Person sx={{ fontSize: 18, color: s.text }} />
                              <Typography fontWeight={700} sx={{ color: s.text, fontSize: '0.9rem' }}>
                                {patient.firstName} {patient.lastName}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {patient.gender}{age ? ` · ${age}y` : ''}
                            </Typography>
                            {adm.admissionDiagnosis && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} noWrap>
                                {adm.admissionDiagnosis}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                              <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                Day {adm.daysInHospital} ·{' '}
                                {new Date(adm.admissionDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                              </Typography>
                              {adm.admittingDoctor && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }} noWrap>
                                  · Dr. {adm.admittingDoctor.lastName}
                                </Typography>
                              )}
                            </Box>
                          </>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 90, gap: 1 }}>
                            <Typography fontWeight={700} sx={{ color: s.text, letterSpacing: 1, fontSize: '0.8rem' }}>
                              {s.label}
                            </Typography>
                            {(bed.status === 'Available' || bed.status === 'Reserved') && canEdit && (
                              <Chip size="small" label="+ Admit" sx={{ cursor: 'pointer', bgcolor: s.border, color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {/* Legend */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {Object.entries(STATUS_STYLE).map(([status, s]) => (
                <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: s.border }} />
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Container>

      {/* ── Patient Quick Panel ── */}
      <Dialog open={patientPanel.open} onClose={() => setPatientPanel({ open: false, bed: null })} maxWidth="sm" fullWidth>
        {patientPanel.bed?.currentAdmission && (() => {
          const adm = patientPanel.bed.currentAdmission;
          const p = adm.patient;
          const age = calcAge(p?.dateOfBirth);
          return (
            <>
              <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{p ? `${p.firstName} ${p.lastName}` : '—'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {p?.gender}{age ? ` · ${age} yrs` : ''} · Bed: {patientPanel.bed?.bedNumber}
                    </Typography>
                  </Box>
                  {adm.isMlc && <Chip label="MLC" color="error" size="small" icon={<PriorityHigh />} />}
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={1.5}>
                  {[
                    ['Admission Type', adm.admissionType],
                    ['Admitted', new Date(adm.admissionDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })],
                    ['Day', `Day ${adm.daysInHospital}`],
                    ['Expected Discharge', adm.expectedDischargeDate ? new Date(adm.expectedDischargeDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : 'Not set'],
                    ['Diagnosis', adm.admissionDiagnosis || '—'],
                    ['Doctor', adm.admittingDoctor ? `Dr. ${adm.admittingDoctor.firstName} ${adm.admittingDoctor.lastName}` : '—'],
                    ['Phone', p?.phoneNumber || '—'],
                  ].map(([label, val]) => (
                    <Grid item xs={6} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={600}>{val}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<OpenInNew />}
                    onClick={() => navigate(`/ipd/patient/${patientPanel.bed?.currentAdmission?.id}`)}>
                    Open Full Record
                  </Button>
                  {canEdit && (
                    <Button variant="contained" color="success" size="small" onClick={() => { setPatientPanel({ open: false, bed: null }); openDischarge(patientPanel.bed); }}>
                      Process Discharge
                    </Button>
                  )}
                </Box>
                <Button onClick={() => setPatientPanel({ open: false, bed: null })}>Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── New Admission Dialog ── */}
      <Dialog open={admitDialog.open} onClose={() => setAdmitDialog({ open: false, prefillBed: null })} maxWidth="md" fullWidth>
        <DialogTitle>Admit Patient — {ward?.name}</DialogTitle>
        <form onSubmit={handleAdmit}>
          <DialogContent>
            {admitError && <Typography color="error" sx={{ mb: 2 }}>{admitError}</Typography>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={patientOptions}
                  getOptionLabel={o => `${o.firstName} ${o.lastName} — ${o.phoneNumber}`}
                  value={admitForm.patientId}
                  onChange={(_, v) => setAdmitForm(p => ({ ...p, patientId: v }))}
                  onInputChange={(_, v) => setPatientSearch(v)}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  renderInput={params => <TextField {...params} label="Patient *" placeholder="Search name or phone" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Bed</InputLabel>
                  <Select value={admitForm.bedId} label="Bed *"
                    onChange={e => setAdmitForm(p => ({ ...p, bedId: e.target.value }))}>
                    {selectableBeds.length === 0
                      ? <MenuItem disabled value="">No available beds</MenuItem>
                      : selectableBeds.map(b => <MenuItem key={b.id} value={String(b.id)}>{b.bedNumber} ({b.bedType}) — {b.status}</MenuItem>)
                    }
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={doctors}
                  getOptionLabel={o => `Dr. ${o.firstName} ${o.lastName}${o.specialization ? ` — ${o.specialization}` : ''}`}
                  value={admitForm.admittingDoctorId}
                  onChange={(_, v) => setAdmitForm(p => ({ ...p, admittingDoctorId: v }))}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  renderInput={params => <TextField {...params} label="Admitting Doctor" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Admission Type</InputLabel>
                  <Select value={admitForm.admissionType} label="Admission Type *"
                    onChange={e => setAdmitForm(p => ({ ...p, admissionType: e.target.value }))}>
                    {ADMISSION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Admission Date & Time" type="datetime-local"
                  InputLabelProps={{ shrink: true }} value={admitForm.admissionDate}
                  onChange={e => setAdmitForm(p => ({ ...p, admissionDate: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Expected Discharge Date" type="date"
                  InputLabelProps={{ shrink: true }} value={admitForm.expectedDischargeDate}
                  onChange={e => setAdmitForm(p => ({ ...p, expectedDischargeDate: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth label="Admission Diagnosis"
                  value={admitForm.admissionDiagnosis}
                  onChange={e => setAdmitForm(p => ({ ...p, admissionDiagnosis: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="ICD-10 Code"
                  value={admitForm.icd10Code}
                  onChange={e => setAdmitForm(p => ({ ...p, icd10Code: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={admitForm.isMlc} onChange={e => setAdmitForm(p => ({ ...p, isMlc: e.target.checked }))} color="error" />}
                  label={<Typography color={admitForm.isMlc ? 'error' : 'text.primary'} fontWeight={admitForm.isMlc ? 700 : 400}>MLC Case</Typography>}
                />
                {admitForm.isMlc && (
                  <TextField fullWidth size="small" label="MLC / FIR Details" value={admitForm.mlcDetails}
                    onChange={e => setAdmitForm(p => ({ ...p, mlcDetails: e.target.value }))} sx={{ mt: 1 }} />
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdmitDialog({ open: false, prefillBed: null })}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={admitting}>
              {admitting ? 'Admitting…' : 'Admit Patient'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Discharge Dialog ── */}
      <Dialog open={dischargeDialog.open} onClose={() => setDischargeDialog({ open: false, bed: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Process Discharge — {dischargeDialog.bed?.currentAdmission?.patient
            ? `${dischargeDialog.bed.currentAdmission.patient.firstName} ${dischargeDialog.bed.currentAdmission.patient.lastName}`
            : ''} ({dischargeDialog.bed?.bedNumber})
        </DialogTitle>
        <form onSubmit={handleDischarge}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Discharge Status</InputLabel>
              <Select value={dischargeForm.status} label="Discharge Status *"
                onChange={e => setDischargeForm(p => ({ ...p, status: e.target.value }))}>
                {['Discharged','LAMA','Transferred','Expired'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Condition at Discharge</InputLabel>
              <Select value={dischargeForm.dischargeType} label="Condition at Discharge"
                onChange={e => setDischargeForm(p => ({ ...p, dischargeType: e.target.value }))}>
                {['Cured','Improved','Same','Deteriorated','LAMA','Transferred','Expired'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Actual Discharge Date & Time" type="datetime-local"
              InputLabelProps={{ shrink: true }} value={dischargeForm.actualDischargeDate}
              onChange={e => setDischargeForm(p => ({ ...p, actualDischargeDate: e.target.value }))} />
            <TextField fullWidth label="Discharge Notes" multiline rows={3}
              value={dischargeForm.dischargeNotes}
              onChange={e => setDischargeForm(p => ({ ...p, dischargeNotes: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDischargeDialog({ open: false, bed: null })}>Cancel</Button>
            <Button type="submit" variant="contained" color="success" disabled={discharging}>
              {discharging ? 'Processing…' : 'Confirm Discharge'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default WardBedView;
